import ReactDOM from 'react-dom';
import {
    inject,
    postConstruct,
    singleton
} from '../../infrastructure/di/annotations';
import {ContentStore, getCurrentPageRootModel} from '../content/ContentStore';
import {RouterService} from '../route/RouterService';
import {isInBrowser} from '../../utils/browser/isInBrowser';
import {FeatureTogglesModel} from '../../../generated/core';
import {Logger} from '../logger/Logger';
import {ModelStore} from '../model/ModelStore';
import {removeExtension} from '../../utils/removeExtension';
import {isViewTransitionSupported, startViewTransition} from './viewTransition';

@singleton('ShowroomNavigator')
export class ShowroomNavigator {
    @inject() private contentStore!: ContentStore;
    @inject() private routerService!: RouterService;
    @inject() private modelStore!: ModelStore;
    @inject() private featureToggles!: FeatureTogglesModel;
    @inject() private logger!: Logger;

    @postConstruct()
    public async initialize() {
        if (
            isInBrowser() &&
            this.featureToggles.showroomTopbarNavigationEnabled
        ) {
            await this.loadAllShowroomPages();
            this.routerService.listen(async () => {
                this.loadAllShowroomPages();
            });
        }
    }

    private async loadAllShowroomPages() {
        const model =
            await this.contentStore.getCurrentPageRootModelAsPromise();

        if (!model?.showroomTopbarNavigationModel?.items) {
            return;
        }
        for (const item of model?.showroomTopbarNavigationModel?.items) {
            const path = removeExtension(item.path);
            this.modelStore.loadContent(path);
        }
    }

    public aroundPathChange(
        oldPathName: string,
        newPathName: string,
        change: () => void
    ) {
        if (!this.featureToggles.showroomTopbarNavigationEnabled) {
            change();
            return;
        }
        const oldPageNavigationItems = getCurrentPageRootModel(
            this.contentStore,
            removeExtension(oldPathName)
        )?.showroomTopbarNavigationModel?.items;

        if (!oldPageNavigationItems) {
            change();
            return;
        }

        const showroomPaths = oldPageNavigationItems.map(i =>
            removeExtension(i.path)
        );

        const newPathInShowroom = showroomPaths.includes(
            removeExtension(newPathName)
        );

        if (newPathInShowroom && isViewTransitionSupported()) {
            this.logger.general.debug(
                `starting a showroom transition from ${oldPathName} to ${newPathName}`
            );
            const timeBefore = Date.now();

            startViewTransition(async () => {
                // execute all state changes and flush the dom. this might take long for big pages.
                ReactDOM.flushSync(() => {
                    change();
                });
                const timeAfter = Date.now();
                this.logger.general.info(
                    `finishing a showroom transition target render in ${timeAfter - timeBefore} ms from ${oldPathName} to ${newPathName} `
                );
            });
        } else {
            change();
        }
    }
}
